import React, {useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {Page} from 'components/common/Page';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {
    resetPasswordByAdminClicked,
    setPasswordResetUser,
    userForPasswordReset$,
} from 'models/password';
import {onSetEditUser} from 'models/profile/staff/table';
import {StaffAccessors as Accessors, StaffListFormattedRow, StaffListRawRow} from 'types/profile';
import {handleNil} from 'utils/handleNil';

import {ProfileNavigationTabs} from '../NavigationTabs';
import {Wrapper} from '../styled';

import {
    CancelConfirmButton,
    ConfirmRemoveFooter,
    CreateUserButton,
    EmployeeNameButton,
    HeaderControls,
    SubmitConfirmButton,
} from './styled';
import {StaffGridTable} from './Table';

const getFullName = ({firstName, middleName, lastName}: StaffListRawRow) =>
    [firstName, middleName, lastName].filter(Boolean).join(' ');

export function convertData(
    data: StaffListRawRow[] | undefined,
    t: TFunction,
    updateUser: () => void,
): StaffListFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [Accessors.id]: handleNil(elem.id),
        [Accessors.name]: handleNil(getFullName(elem), (name: string) => (
            <EmployeeNameButton
                type="button"
                onClick={() => {
                    onSetEditUser(elem);
                    updateUser();
                }}
            >
                {name}
            </EmployeeNameButton>
        )),
        [Accessors.email]: handleNil(elem.email),
        [Accessors.phone]: handleNil(elem.phone),
        [Accessors.storageLocation]: handleNil(elem.storageLocation),
    }));
}

const getConfig = (t: TFunction) => [
    {
        id: Accessors.name,
        header: t('pages.profile.staff.name'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    },
    {
        id: Accessors.email,
        header: t('pages.profile.staff.email'),
        // filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    },
    {
        id: Accessors.phone,
        header: t('pages.profile.staff.phone'),
        // filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
        width: 300,
    },
    {
        id: Accessors.storageLocation,
        header: t('pages.profile.staff.storageLocation'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    },
];

export const Staff = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const permissions = useStore(permissions$);
    const userToReset = useStore(userForPasswordReset$);
    const canCreateUser = useMemo(
        () =>
            permissions.includes(permissions.userOwnRegister) ||
            permissions.includes(permissions.userAllRegister),
        [permissions],
    );

    const CreateBtn = () => {
        if (!canCreateUser) return null;

        return (
            <CreateUserButton size="sm" onClick={() => history.push(ROUTE.STAFF_CREATE)}>
                + {t('pages.profile.staff.create.createBtn')}
            </CreateUserButton>
        );
    };

    const ConfirmPasswordReset = () => {
        if (!userToReset) return null;

        const onConfirm = () => resetPasswordByAdminClicked(userToReset);

        const translate = (key: string) => t('pages.profile.staff.confirmPasswordReset.' + key);

        return (
            <Modal
                modalWidth={0}
                show={!!userToReset}
                hideCloseButton
                onClose={() => setPasswordResetUser(null)}
            >
                <p style={{textAlign: 'center', fontWeight: 'bold'}}>{translate('title')}</p>
                <p style={{textAlign: 'center'}}>
                    {t('pages.profile.staff.confirmPasswordReset.message', {
                        name: userToReset.firstName,
                    })}
                </p>
                <ConfirmRemoveFooter>
                    <CancelConfirmButton
                        variant={ButtonVariant.Outline}
                        onClick={() => setPasswordResetUser(null)}
                    >
                        {translate('cancel')}
                    </CancelConfirmButton>
                    <SubmitConfirmButton onClick={onConfirm}>{translate('ok')}</SubmitConfirmButton>
                </ConfirmRemoveFooter>
            </Modal>
        );
    };

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    <StaffGridTable
                        configFunc={getConfig}
                        dataMapper={convertData}
                        headerControls={
                            <HeaderControls>
                                <CreateBtn />
                            </HeaderControls>
                        }
                    />
                </ContentContainer>
            </Wrapper>
            {userToReset && <ConfirmPasswordReset />}
        </Page>
    );
};
